import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { DollarSign, Loader2, TrendingUp } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CostByModel {
  model_name: string;
  cost_usd: number;
  requests: number;
}

interface CostByDepartment {
  department: string;
  cost_usd: number;
}

interface MonthlyTrend {
  month: string;
  cost_usd: number;
}

interface CostResponse {
  total_spend_mtd: number;
  daily_average: number;
  projected_monthly: number;
  budget_remaining: number;
  budget_total: number;
  cost_by_model: CostByModel[];
  cost_by_department: CostByDepartment[];
  monthly_trend: MonthlyTrend[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function budgetColor(remaining: number, total: number): string {
  if (total === 0) return 'text-foreground';
  const pct = remaining / total;
  if (pct > 0.3) return 'text-green-500';
  if (pct > 0.1) return 'text-yellow-500';
  return 'text-red-500';
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function CostTrackingPage() {
  const costQuery = useQuery<CostResponse>({
    queryKey: ['governance', 'costs'],
    queryFn: () => api<CostResponse>('governance/models', { query: { scope: 'costs' } }),
  });

  const data = costQuery.data;
  const maxModelCost = data ? Math.max(...data.cost_by_model.map((m) => m.cost_usd), 1) : 1;
  const maxDeptCost = data ? Math.max(...data.cost_by_department.map((d) => d.cost_usd), 1) : 1;
  const maxMonthCost = data ? Math.max(...data.monthly_trend.map((m) => m.cost_usd), 1) : 1;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <DollarSign className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">AI Cost Tracking</h1>
          <p className="text-muted-foreground mt-1">Monitor AI spending across models, teams, and time periods.</p>
        </div>
      </div>

      {/* Stats */}
      {costQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />)}
        </div>
      ) : costQuery.isError ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-6 text-center">
          <p className="text-sm text-red-500">
            Failed to load cost data.{' '}
            <button type="button" onClick={() => costQuery.refetch()} className="underline hover:no-underline">Retry</button>
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-sm text-muted-foreground">Total Spend MTD</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(data!.total_spend_mtd)}</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-sm text-muted-foreground">Daily Average</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(data!.daily_average)}</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-sm text-muted-foreground">Projected Monthly</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-foreground">{formatCurrency(data!.projected_monthly)}</p>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-sm text-muted-foreground">Budget Remaining</p>
              <p className={`text-2xl font-bold ${budgetColor(data!.budget_remaining, data!.budget_total)}`}>
                {formatCurrency(data!.budget_remaining)}
              </p>
            </div>
          </div>

          {/* Cost by model */}
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold text-foreground mb-4">Cost by Model</h2>
            {data!.cost_by_model.length === 0 ? (
              <p className="text-sm text-muted-foreground">No model usage data available.</p>
            ) : (
              <div className="space-y-3">
                {data!.cost_by_model.map((model) => (
                  <div key={model.model_name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">{model.model_name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">{model.requests.toLocaleString()} requests</span>
                        <span className="text-sm font-medium text-foreground">{formatCurrency(model.cost_usd)}</span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${(model.cost_usd / maxModelCost) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cost by department */}
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold text-foreground mb-4">Cost by Department</h2>
            {data!.cost_by_department.length === 0 ? (
              <p className="text-sm text-muted-foreground">No department cost data available.</p>
            ) : (
              <div className="space-y-3">
                {data!.cost_by_department.map((dept) => (
                  <div key={dept.department}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">{dept.department}</span>
                      <span className="text-sm font-medium text-foreground">{formatCurrency(dept.cost_usd)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all"
                        style={{ width: `${(dept.cost_usd / maxDeptCost) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Monthly trend */}
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold text-foreground mb-4">Monthly Trend (Last 6 Months)</h2>
            {data!.monthly_trend.length === 0 ? (
              <p className="text-sm text-muted-foreground">No historical data available.</p>
            ) : (
              <div className="flex items-end gap-3 h-48">
                {data!.monthly_trend.map((month) => {
                  const heightPct = maxMonthCost > 0 ? (month.cost_usd / maxMonthCost) * 100 : 0;
                  return (
                    <div key={month.month} className="flex-1 flex flex-col items-center justify-end h-full">
                      <span className="text-xs font-medium text-foreground mb-1">{formatCurrency(month.cost_usd)}</span>
                      <div
                        className="w-full rounded-t-md bg-primary/80 hover:bg-primary transition-colors min-h-[4px]"
                        style={{ height: `${Math.max(heightPct, 2)}%` }}
                      />
                      <span className="text-xs text-muted-foreground mt-2">{month.month}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Loading state for content sections */}
      {costQuery.isLoading && (
        <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Loading cost analytics...</span>
        </div>
      )}
    </div>
  );
}
